import { TileContainer } from "~/Configurators/common/components/GenericTileSubstep/GenericTileSubstep";
import classes from "./ShaftMachiningSubstep.module.less";
import SubstepFrame from "~/Configurators/Linear/components/SubstepFrame";
import { useConfiguratorType } from "~/Configurators/common/useConfiguratorType";
import { ConfiguratorType, type ShaftConfiguratorType } from "~/Configurators/ConfiguratorType";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { type Kmat, type LinearSubstep, type SubstepTile } from "~/Configurators/Linear/models";
import { LinearTileV2 } from "~/Configurators/Shaft/substeps/LinearTileV2";
import { TileCarousel } from "~/Configurators/Shaft/substeps/ShaftMachiningSubstep/TileCarousel/TileCarousel";
import { useMemo } from "react";
import { isTileEnabled, isTileStandard } from "~/Configurators/common/tileUtils";
import { type TFunction } from "i18next";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { updateCsticValuesFromTile } from "~/Configurators/Linear/redux/actions";
import { type CustomDispatch } from "~/redux/types";
import { type RootState } from "~/redux/store";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { useDispatch, useSelector } from "react-redux";
import { getKmats, selectConflictedCstics, selectMainKmatCstics } from "~/Configurators/Linear/redux/selectors";
import { formatCsticsToSet } from "~/Configurators/Linear/utils";
import { URL_CONTACT_US } from "~/utils/url/staticUrls";
import * as MetricCstics from "~/Configurators/Shaft/metric/metricCsticConstants";
import * as InchCstics from "~/Configurators/Shaft/inch/inchCsticConstants";
import { InchShaftChamferSegment } from "~/Configurators/Shaft/substeps/ShaftMachiningSubstep/components/InchShaftChamferSegment/InchShaftChamferSegment";
import { InchShaftRadialThreadSegment } from "~/Configurators/Shaft/substeps/ShaftMachiningSubstep/components/InchShaftRadialThreadSegment/InchShaftRadialThreadSegment";
import { showChamferedSection, showRadialThreadSection } from "~/Configurators/Shaft/shaftConfiguratorHelper";
import { useTranslation } from "react-i18next";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import classNames from "classnames";

interface Props {
  // Actually substepIndex
  csticIndex: number;
  // Actually substepAmount
  csticAmount: number;
  def: LinearSubstep;
}

type Side = "left" | "center" | "right";
const SIDES: Side[] = ["left", "center", "right"];

const CONFIGURATION = {
  [ConfiguratorType.MetricShaft]: {
    cstics: [
      MetricCstics.SHAFT_MACHINING_LEFT,
      MetricCstics.SHAFT_MACHINING_MIDDLE,
      MetricCstics.SHAFT_MACHINING_RIGHT,
    ],
    titles: [
      "configurator.shaft.step.2.substep.3.sectionTitle.left",
      "configurator.shaft.step.2.substep.3.sectionTitle.middle",
      "configurator.shaft.step.2.substep.3.sectionTitle.right",
    ],
    maxVisiblePerSide: 2,
  },
  [ConfiguratorType.InchShaft]: {
    cstics: [InchCstics.SHAFT_MACHINING_LEFT, InchCstics.SHAFT_MACHINING_MIDDLE, InchCstics.SHAFT_MACHINING_RIGHT],
    titles: [
      "configurator.shaft.step.2.substep.3.sectionTitle.left",
      "configurator.shaft.step.2.substep.3.sectionTitle.middle",
      "configurator.shaft.step.2.substep.3.sectionTitle.right",
    ],
    maxVisiblePerSide: 1,
  },
} as const;

const Carousel = TileCarousel<SubstepTile & { value: string; side: Side }>;

export const ZERO_TILE_ALWAYS_ENABLED = false;

export const ShaftMachiningSubstep = ({ def, ...props }: Props) => {
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const configuratorType = useConfiguratorType() as ShaftConfiguratorType;

  const config = CONFIGURATION[configuratorType];
  const csticsList = config.cstics;

  const kmat = useSelector(selectMainKmatCstics);
  const kmatMap = useSelector(getKmats);
  const conflictedCstics = useSelector(selectConflictedCstics);
  const xmlT = useXmlConfiguratorTranslations().t!;
  const { t } = useTranslation();
  const getValueForCarousel = (csticName: string) => {
    return kmat[csticName]?.value ?? "";
  };

  const leftCstic = kmat[csticsList[0]];
  const middleCstic = kmat[csticsList[1]];
  const rightCstic = kmat[csticsList[2]];

  const isLeftSideZeroSelected = leftCstic?.value === "000" && leftCstic?.values.length > 1;
  const isRightSideZeroSelected = rightCstic?.value === "000" && rightCstic?.values.length > 1;

  const isValueEnabled = (csticName: string, tile: SubstepTile) => {
    const value = tile.cstics[csticName]!;

    // MIDDLE GROUND VARIANT
    if (ZERO_TILE_ALWAYS_ENABLED) {
      if (kmat[csticName]?.value === value) {
        // Selected should always be enabled
        return true;
      }
      if (isLeftSideZeroSelected && csticName !== csticsList[0]) {
        return false;
      }
      if (isRightSideZeroSelected && csticName !== csticsList[2]) {
        return false;
      }
      return isTileEnabled(tile, kmatMap);
    } else {
      // DEFAULT VARIANT
      return kmat[csticName]?.values.find((val) => val.value === value) ?? false;
    }
  };

  const tileGroups = useMemo(() => {
    const tileGroups = def.groupedTiles.map((tileGroup, index) => {
      const csticName = csticsList[index];
      const tiles = tileGroup.tiles.map((tile) => ({
        ...tile,
        value: tile.cstics[csticName]!,
        side: SIDES[index],
        disabled: !isValueEnabled(csticName, tile),
      }));
      shiftTiles(tiles, kmatMap);

      return {
        ...tileGroup,
        tiles,
        cstic: csticsList[index],
        name: SIDES[index],
      };
    });
    return tileGroups;
  }, [def.groupedTiles, configuratorType, kmat]);

  const makeOnChange = (index: number) => (value: string) => {
    const tile = tileGroups[index].tiles.find((tile) => tile.value === value)!;
    dispatch(updateCsticValuesFromTile(def.type, null, tile));
  };

  const isAllSelected = !!leftCstic?.value && !!middleCstic?.values && !!rightCstic?.value;

  const isInchShaft = configuratorType === ConfiguratorType.InchShaft;

  const showChamferedLeft = isInchShaft && showChamferedSection(leftCstic?.value);
  const showRadialThreadCenter = isInchShaft && showRadialThreadSection(middleCstic?.value);
  const showChamferedRight = isInchShaft && showChamferedSection(rightCstic?.value);

  const showExtraOptions = showChamferedLeft || showChamferedRight || showRadialThreadCenter;

  const isCenterDisabled = !leftCstic?.value || !rightCstic?.value;

  return (
    <SubstepFrame
      title={def.name}
      description=""
      info={def.info}
      isPicked={isAllSelected}
      errorMessage={def.isInvalid ? t("linear.step.select.option") : ""}
      showError={def.isInvalid}
      {...props}
    >
      <TileContainer columnAmount={4} className={classes.tileContainer}>
        {tileGroups.map((tileGroup, index) => (
          <Stacker gap={8} key={tileGroup.name}>
            <LanguageAwareExternalContent
              content={config.titles[index]}
              loadedClassname={classNames("scha__lc__external-label--shaft", classes.carouselTitle)}
            />
            <Carousel
              values={tileGroup.tiles}
              maxVisiblePerSide={config.maxVisiblePerSide}
              renderTile={renderTile}
              value={getValueForCarousel(tileGroup.cstic!)}
              onChange={makeOnChange(index)}
              error={conflictedCstics.includes(csticsList[index])}
              disabled={index === 1 && isCenterDisabled}
            />
          </Stacker>
        ))}
      </TileContainer>
      {!isInchShaft && (
        <TileContainer columnAmount={4} className={classes.tileContainer}>
          <LinearTileV2.Base href={URL_CONTACT_US} target={"_blank"} className={classes.otherTile}>
            <LinearTileV2.Headline content={"configurator.shaft.step.2.substep.3.furtherOptions.title"}>
              <LinearTileV2.Info info={"configurator.shaft.step.2.substep.3.furtherOptions.infoPopup"} />
            </LinearTileV2.Headline>
            <LinearTileV2.SubHeadline content={"\xa0"} />
          </LinearTileV2.Base>
        </TileContainer>
      )}

      {showExtraOptions && (
        <TileContainer columnAmount={4} className={classes.tileContainer}>
          <div>{showChamferedLeft && <InchShaftChamferSegment side={"left"} />}</div>
          <div>{showRadialThreadCenter && <InchShaftRadialThreadSegment />}</div>
          <div>{showChamferedRight && <InchShaftChamferSegment side={"right"} />}</div>
        </TileContainer>
      )}
    </SubstepFrame>
  );
};

const renderTile = (props: {
  data: SubstepTile & { value: string; side: Side };
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) => <CarouselTile {...props} />;

const CarouselTile = ({
  data: { value, ...tile },
  selected,
  onClick,
  error,
  disabled,
}: {
  data: SubstepTile & { value: string; side: Side; disabled?: boolean };
  selected?: boolean;
  onClick?: () => void;
  error?: boolean;
  disabled?: boolean;
}) => {
  const kmats = useSelector(getKmats);

  const isStandard = isTileStandard(tile, kmats);
  const debugLanguage = isDebugLanguageEnabled();
  return (
    <LinearTileV2.Base
      selected={selected}
      style={{ height: 200, width: "100%" }}
      onClick={!selected ? onClick : undefined}
      disabled={disabled || tile.disabled}
      error={error}
    >
      <LinearTileV2.CheckedIndicator />
      <LinearTileV2.StandardIndicator style={{ visibility: isStandard ? undefined : "hidden" }} />
      <LinearTileV2.Image ratio="322/80" tKey={tile.imageUrl} />
      <LinearTileV2.Headline content={debugLanguage ? formatCsticsToSet(tile.cstics) : tile.name}>
        <LinearTileV2.Info
          className={"m-0"}
          info={tile.info}
          popupClassName={"scha__lc__external-content--shaft__modal"}
        />
      </LinearTileV2.Headline>
      <LinearTileV2.SubHeadline content={tile.description} />
    </LinearTileV2.Base>
  );
};

/**
 * Format of translation keys:
 * configurator.shaft.step.2.substep.3.tile.007.title
 * configurator.shaft.step.2.substep.3.tile.007.description
 * configurator.shaft.step.2.substep.3.tile.007.image.left
 * configurator.shaft.step.2.substep.3.tile.007.image.right
 * OR
 * configurator.shaft.step.2.substep.3.tile.007.image
 */
function getTranslations(t: TFunction, tileValue: string, side: Side) {
  const getKey = (key: string) => `configurator.shaft.step.2.substep.3.tile.${tileValue}.${key}`;
  return {
    title: t(getKey("title")),
    description: t(getKey("description")),
    image: t([getKey(`image.${side}`), getKey("image")]),
  };
}

function shiftTiles(tiles: SubstepTile[], kmatsMap: Dict<Kmat[]>) {
  const index = tiles.findIndex((tile) => isTileStandard(tile, kmatsMap));
  const element = tiles.splice(index, 1)[0];
  tiles.unshift(element);
  return tiles;
}
